import { CreditCard } from "account/_shared/models/CreditCard";
import { API, Auth } from "aws-amplify";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Button from "_shared/components/Button/Button";
import Loading from "_shared/components/Loading/Loading";
import { useModal } from "_shared/contexts/ModalContext";
import AddCardWithElements from "../AddCardModal/AddCardModal";

function Cards() {
  const history = useHistory();
  const { awaitConfirmation } = useModal();

  const [cardsList, setCardsList] = useState<CreditCard[]>();
  // id of card currently being set as primary
  const [primaryCardLoading, setPrimaryCardLoading] = useState<string | null>();
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    const cardsRes: CreditCard[] = await API.post(
      "auth",
      "/cognito/listCards",
      {},
    );
    setCardsList(cardsRes.sort((a: any, b: any) => b.default - a.default));
  };

  const setPrimaryCard = async (cardId: string) => {
    setPrimaryCardLoading(cardId);
    await API.post("auth", "/cognito/setPrimary", {
      body: { payment_method_id: cardId },
    });
    const user = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(user, {
      "custom:payment_method_id": cardId,
    });
    await fetchCards();
    setPrimaryCardLoading(null);
  };

  const removeCard = async (selectedCard: CreditCard) => {
    const handleRemoveCard = async () => {
      await API.post("auth", "/cognito/deleteCard", {
        body: { payment_method_id: selectedCard.id },
      });
      setCardsList(
        (prev) => prev && prev.filter((card) => card.id !== selectedCard.id),
      );
    };
    awaitConfirmation(
      handleRemoveCard,
      `Are you sure you want to remove your ${selectedCard.brand.toUpperCase()} card ending in ${
        selectedCard ? selectedCard?.last4 : null
      }?`,
    );
  };

  return (
    <>
      <div className="payment-text">
        Payment{" "}
        <span className="credit-and-debit-cards-text">(Max 3 cards)</span>
      </div>
      {!cardsList ? (
        <Loading variant="inner" />
      ) : (
        cardsList.map((card) => {
          return (
            <div
              data-testid="card-row"
              key={card.id}
              className="credit-card-on-file"
            >
              <i
                className={
                  card.brand === "visa"
                    ? "fab fa-cc-visa"
                    : card.brand === "mastercard"
                    ? "fab fa-cc-mastercard"
                    : card.brand === "discover"
                    ? "fab fa-cc-discover"
                    : card.brand === "amex"
                    ? "fab fa-cc-amex"
                    : "far fa-credit-card"
                }
              ></i>
              <div className="card-last-numbers" style={{ paddingLeft: "5px" }}>
                {card.brand.toUpperCase()} ending in {card?.last4}
              </div>
              <div className="card-expire">{card.expiration}</div>
              <div className="edit-buttons">
                {card.default ? (
                  <div style={{ fontWeight: "bold", color: "#75ce41" }}>
                    Primary
                  </div>
                ) : (
                  <button
                    data-testid="button-make-primary"
                    style={{
                      border: "none",
                      background: "none",
                      textAlign: "center",
                      cursor: "pointer",
                      paddingRight: 20,
                      color: "white",
                    }}
                    onClick={() => {
                      if (primaryCardLoading) return;
                      setPrimaryCard(card.id);
                    }}
                  >
                    Make Primary
                  </button>
                )}
                {card.default ? null : (
                  <button
                    data-testid="button-remove-card"
                    onClick={() => removeCard(card)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#d33541",
                    }}
                  >
                    Remove
                  </button>
                )}
                {card.id === primaryCardLoading && (
                  <div
                    style={{
                      position: "absolute",
                      transform: "scale(0.4)",
                      marginRight: 150,
                      marginTop: 5,
                    }}
                  >
                    <Loading variant="inner" />
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      <Button
        data-testid="button-add-card"
        onClick={() => {
          setAddCardModalOpen(true);
        }}
      >
        Add a new card
      </Button>
      <AddCardWithElements
        open={addCardModalOpen}
        onClose={() => setAddCardModalOpen(false)}
        onSuccess={() => {
          fetchCards();
          setAddCardModalOpen(false);
        }}
      />
    </>
  );
}

export default Cards;
